// components/RequireLoginModal.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaTimes, FaSignInAlt, FaUserPlus } from '../utils/iconUtils';

interface RequireLoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  programId?: string;
  programTitle?: string;
}

const RequireLoginModal: React.FC<RequireLoginModalProps> = ({ 
  isOpen, 
  onClose, 
  programId,
  programTitle 
}) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleLogin = () => {
    onClose();
    navigate('/login', { 
      state: { 
        from: programId ? `/programs/${programId}/register` : '/programs',
        programTitle 
      } 
    });
  };

  const handleSignup = () => {
    onClose();
    navigate('/register', { 
      state: { 
        from: programId ? `/programs/${programId}/register` : '/programs',
        programTitle 
      } 
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-xl shadow-2xl max-w-md w-full"
      >
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Registration Required</h2>
            {programTitle && (
              <p className="text-sm text-gray-600 mt-1">for {programTitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <FaTimes className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaSignInAlt className="h-8 w-8 text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Login Required
            </h3>
            <p className="text-gray-600">
              You need to have an account to register for this program. Please login or create a new account.
            </p>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={handleLogin}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              <FaSignInAlt className="h-5 w-5" />
              Login to Existing Account
            </button>
            
            <button
              onClick={handleSignup}
              className="w-full flex items-center justify-center gap-3 px-4 py-3 border-2 border-primary-600 text-primary-600 rounded-lg font-medium hover:bg-primary-50 transition-colors"
            >
              <FaUserPlus className="h-5 w-5" />
              Create New Account
            </button>
          </div>
          
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 text-center">
              Having trouble? Contact us at support@aif.org
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RequireLoginModal;